import {
  format,
  formatDistanceToNow,
  formatDistanceToNowStrict,
  differenceInMinutes,
  differenceInHours,
  differenceInDays,
  differenceInWeeks,
  differenceInMonths,
  differenceInYears,
} from 'date-fns'
import { enGB, de } from 'date-fns/locale'

const LANGS = {
  en: enGB,
  de,
}

// Stamp shown in the tooltip behind a compact timestamp, e.g. "2026-04-22 - 15:03".
const ABSOLUTE_DATE_TIME_FORMAT = 'yyyy-MM-dd - HH:mm'

// Compact "time ago" units, largest first. Each entry is probed in order and the
// first one that has fully elapsed wins, so a timestamp always renders as a
// single unit ("19 h", never "19 h 12 min"). `unit` is the i18n key suffix —
// see `date.compact.*` in the language files.
const COMPACT_UNITS = [
  { unit: 'year', difference: differenceInYears },
  { unit: 'month', difference: differenceInMonths },
  { unit: 'week', difference: differenceInWeeks },
  { unit: 'day', difference: differenceInDays },
  { unit: 'hour', difference: differenceInHours },
  { unit: 'minute', difference: differenceInMinutes },
]

// Unit used below one minute — and for timestamps that sit slightly in the
// future, which happens whenever the client clock runs ahead of the server.
const COMPACT_UNIT_NOW = 'now'

const COMPACT_DISTANCE_I18N_PREFIX = 'date.compact.'

// Every unit getCompactDistance can return, so callers (and the translation
// test) can enumerate the keys instead of hard-coding them.
export const COMPACT_DISTANCE_UNITS = [COMPACT_UNIT_NOW, ...COMPACT_UNITS.map(({ unit }) => unit)]

export function compactDistanceKey(unit) {
  return `${COMPACT_DISTANCE_I18N_PREFIX}${unit}`
}

export function formatDate(date, formatStr, lang) {
  return format(date, formatStr ?? 'PPPP', {
    locale: LANGS[lang] ?? LANGS.en,
  })
}

export function formatDateRelativeNowStrict(date, opts = {}) {
  return formatDistanceToNowStrict(date, {
    ...opts,
    locale: LANGS[opts.lang] ?? LANGS.en,
  })
}

export function formatDateRelativeNow(date, opts = {}) {
  return formatDistanceToNow(date, {
    ...opts,
    locale: LANGS[opts.lang] ?? LANGS.en,
  })
}

/**
 * Coarsest elapsed unit between `date` and `now`, LinkedIn/Bluesky style.
 * Returns `{ unit, count }` rather than a string so the caller translates it
 * (see RelativeTime.vue); `unit` is 'now' below a minute, with count 0.
 */
export function getCompactDistance(date, now = new Date()) {
  for (const { unit, difference } of COMPACT_UNITS) {
    const count = difference(now, date)
    if (count >= 1) return { unit, count }
  }
  return { unit: COMPACT_UNIT_NOW, count: 0 }
}

export function formatDateTimeAbsolute(date, lang) {
  return formatDate(date, ABSOLUTE_DATE_TIME_FORMAT, lang)
}
